/**
 * Cross-deal Heartbeat push admin page — every push attempt across
 * every deal, in one table. Used during rollout to spot gaps, failures,
 * or unexpected skips.
 *
 * Backing API: `GET /aws/heartbeat/pushes` (see useAllHeartbeatPushes).
 *
 * Read-only. Pagination is client-side over the BE-imposed cap (default
 * 200 rows, max 1000). For a deeper dive on a specific deal, click the
 * deal name to jump to that deal's Agreements → Heartbeat Preview tab,
 * which renders the per-deal timeline.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AllPushesFilters,
  useAllHeartbeatPushes,
} from 'api/aws/heartbeat/useAllHeartbeatPushes';
import type { HeartbeatPushEntity } from 'api/aws/heartbeat/useHeartbeatPushTimeline';
import { useEntitiesFromSearch } from 'api/search';
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from 'theme/components/PageContainer/PageContainer';

/** Matches the offset PageContainer uses when the global nav is pinned.
 *  Without this, the page header sits under the bar on first paint. */
const NAV_PINNED_OFFSET_PX = 55;

/** Minimal shape we need off org/deal lookups. */
interface OrgLite {
  PK?: string;
  name?: string;
}
interface DealLite {
  PK?: string;
  SK?: string;
  name?: string;
  type?: string;
  stage?: string;
  status?: string;
  dealStatus?: string;
}

type StatusFilter = 'all' | 'success' | 'failure' | 'skipped' | 'resolved';
type FlowFilter = 'all' | 'A' | 'B' | 'IO' | 'none';

const HeartbeatPushesAdmin: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const layout = useLayoutContext();
  const isPinned = layout?.pinned ?? false;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [flowFilter, setFlowFilter] = useState<FlowFilter>('all');
  const [triggerFilter, setTriggerFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  // Filters that we send to the BE (server-side filtering for the
  // structured ones). Free-text search stays client-side over the
  // returned page.
  const apiFilters: AllPushesFilters = useMemo(
    () => ({
      status: statusFilter === 'all' ? undefined : statusFilter,
      flow: flowFilter === 'all' ? undefined : flowFilter,
      trigger: triggerFilter === 'all' ? undefined : triggerFilter,
      limit: 500,
    }),
    [statusFilter, flowFilter, triggerFilter]
  );

  const { data, isLoading, error, refetch, isRefetching } =
    useAllHeartbeatPushes(apiFilters);
  const pushes = data?.pushes ?? [];

  // Entity lookups — resolve org_pk / deal_sk to human-readable names.
  // Same hook the rest of the app uses for org/deal autocompletes etc.,
  // so it shares the cache and stays fresh on rename.
  const { organizations, deals } = useEntitiesFromSearch();
  const orgByPK = useMemo(() => {
    const m = new Map<string, OrgLite>();
    for (const o of (organizations ?? []) as OrgLite[]) {
      if (o.PK) m.set(o.PK, o);
    }
    return m;
  }, [organizations]);
  const dealBySK = useMemo(() => {
    const m = new Map<string, DealLite>();
    for (const d of (deals ?? []) as DealLite[]) {
      if (d.SK) m.set(d.SK, d);
    }
    return m;
  }, [deals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pushes;
    return pushes.filter((p) => {
      const org = orgByPK.get(p.org_pk);
      const deal = dealBySK.get(p.deal_sk);
      const haystack = [
        p.deal_sk,
        p.org_pk,
        org?.name ?? '',
        deal?.name ?? '',
        deal?.type ?? '',
        p.pushed_by,
        p.trigger,
        p.flow,
        p.heartbeat_account_id != null
          ? `account ${p.heartbeat_account_id}`
          : '',
        p.error_message ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [pushes, search, orgByPK, dealBySK]);

  // Unique trigger values present in this dataset — populates the
  // trigger dropdown without hardcoding.
  const triggerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of pushes) if (p.trigger) set.add(p.trigger);
    return Array.from(set).sort();
  }, [pushes]);

  const counts = useMemo(() => {
    let success = 0;
    let failure = 0;
    let skipped = 0;
    let resolved = 0;
    for (const p of filtered) {
      // A resolved skip/failure (a later re-push fixed it) leaves the
      // skipped/failure buckets and counts as resolved instead.
      if (p.resolved) resolved++;
      else if (p.skip_reason) skipped++;
      else if (p.success) success++;
      else failure++;
    }
    return { success, failure, skipped, resolved, total: filtered.length };
  }, [filtered]);

  return (
    <PageContainer
      autoOverflow
      height="calc(100vh - 60px)"
      sx={{ bgcolor: theme.palette.background.default }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1600,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          // Bump top padding by the nav offset when pinned so the page
          // header clears the global bar (same convention as BulkPatch).
          pt: {
            xs: isPinned ? `${NAV_PINNED_OFFSET_PX + 16}px` : 2,
            md: isPinned ? `${NAV_PINNED_OFFSET_PX + 24}px` : 3,
          },
          pb: { xs: 3, md: 4 },
          transition: 'padding 0.3s ease-in',
        }}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              Heartbeat push log
            </Typography>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                disabled={isRefetching}
                size="small"
              >
                <RefreshIcon
                  sx={{
                    fontSize: 20,
                    animation: isRefetching
                      ? 'spin 1s linear infinite'
                      : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Every Grobotic → Heartbeat push attempt across every deal —
            successes, skips, and failures. Use during rollout to spot gaps.
            Read-only.
          </Typography>
        </Stack>

        {/* ── Counts summary ─────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <CountStat
              label="Total"
              value={counts.total}
              color="text.primary"
            />
            <CountStat
              label="Pushed"
              value={counts.success}
              color="success.main"
            />
            <CountStat
              label="Failed"
              value={counts.failure}
              color="error.main"
            />
            <CountStat
              label="Skipped"
              value={counts.skipped}
              color="warning.main"
            />
            <CountStat
              label="Resolved"
              value={counts.resolved}
              color="info.main"
            />
          </Stack>
        </Paper>

        {/* ── Filters ────────────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: 'divider' }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            useFlexGap
            flexWrap="wrap"
          >
            <TextField
              size="small"
              placeholder="Search deal SK, org PK, user, error…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={statusFilter}
              onChange={(_, v) => v && setStatusFilter(v)}
            >
              <ToggleButton value="all">All status</ToggleButton>
              <ToggleButton value="success">Success</ToggleButton>
              <ToggleButton value="failure">Failure</ToggleButton>
              <ToggleButton value="skipped">Skipped</ToggleButton>
              <ToggleButton value="resolved">Resolved</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={flowFilter}
              onChange={(_, v) => v && setFlowFilter(v)}
            >
              <ToggleButton value="all">All flows</ToggleButton>
              <ToggleButton value="A">A</ToggleButton>
              <ToggleButton value="B">B</ToggleButton>
              <ToggleButton value="IO">IO</ToggleButton>
              <ToggleButton value="none">none</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              select
              label="Trigger"
              value={triggerFilter}
              onChange={(e) => setTriggerFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All</MenuItem>
              {triggerOptions.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>

        {/* ── Loading / error states ─────────────────────────────── */}
        {isLoading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Scanning MOTHERSHIP for heartbeat_push entities…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <Alert severity="info">No pushes match the current filters.</Alert>
        )}

        {/* ── Push rows ─────────────────────────────────────────── */}
        {!isLoading && !error && filtered.length > 0 && (
          <Stack spacing={1}>
            {filtered.map((p) => (
              <PushRow
                key={p.SK}
                push={p}
                org={orgByPK.get(p.org_pk)}
                deal={dealBySK.get(p.deal_sk)}
                onJumpToDeal={navigate}
              />
            ))}
          </Stack>
        )}
      </Box>
    </PageContainer>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Subcomponents
// ════════════════════════════════════════════════════════════════════════

const CountStat: React.FC<{
  label: string;
  value: number;
  color: string;
}> = ({ label, value, color }) => {
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, color, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
};

const PushRow: React.FC<{
  push: HeartbeatPushEntity;
  org?: OrgLite;
  deal?: DealLite;
  onJumpToDeal: (path: string) => void;
}> = ({ push, org, deal, onJumpToDeal }) => {
  // A resolved entry (a later re-push for the same deal succeeded) reads as
  // "Resolved" rather than Skipped/Failed, and drops its warning/error color.
  const isResolved = Boolean(push.resolved);
  const isSkipped = Boolean(push.skip_reason) && !isResolved;
  const isSuccess = push.success && !push.skip_reason;
  const statusColor = isResolved
    ? 'info'
    : isSuccess
      ? 'success'
      : isSkipped
        ? 'warning'
        : 'error';
  const StatusIcon = isResolved || isSuccess
    ? CheckCircleIcon
    : isSkipped
      ? WarningIcon
      : ErrorIcon;
  const priorReason = push.skip_reason
    ? `skipped: ${push.skip_reason}`
    : 'failed';
  const statusLabel = isResolved
    ? `Resolved (was ${priorReason})`
    : isSuccess
      ? 'Pushed'
      : isSkipped
        ? `Skipped (${push.skip_reason})`
        : 'Failed';

  // Resolved display labels with fallbacks. Org/deal are loaded async by
  // useEntitiesFromSearch — show the resolved name when we have it,
  // otherwise the raw key so the row is still useful.
  const orgLabel = org?.name ?? push.org_pk;
  const dealLabel = deal?.name ?? push.deal_sk;

  return (
    <Accordion
      disableGutters
      square
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        '&:before': { display: 'none' },
        bgcolor: 'background.paper',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 52,
          '& .MuiAccordionSummary-content': {
            my: 0.5,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '120px 80px 1fr auto',
              md: '120px 80px 140px 1fr auto',
            },
            alignItems: 'center',
            columnGap: 1.25,
            rowGap: 0.5,
          },
        }}
      >
        <Chip
          icon={<StatusIcon />}
          label={statusLabel}
          color={statusColor}
          size="small"
          variant={isSuccess ? 'filled' : 'outlined'}
          sx={{ justifySelf: 'start', maxWidth: '100%' }}
        />
        <Box sx={{ justifySelf: 'start' }}>
          {push.flow !== 'none' && (
            <Chip
              label={`Flow ${push.flow}`}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
            />
          )}
        </Box>
        <Box
          sx={{ justifySelf: 'start', display: { xs: 'none', md: 'block' } }}
        >
          {push.trigger !== 'unknown' && (
            <Chip
              label={push.trigger}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', bgcolor: 'action.hover' }}
            />
          )}
        </Box>

        {/* Main info column — two-line layout with org → deal on top,
            metadata (date · submissions · type) below in muted text. */}
        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ minWidth: 0 }}
          >
            <Tooltip title={push.org_pk} placement="top-start">
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 600, fontSize: 13, minWidth: 0 }}
              >
                {orgLabel}
              </Typography>
            </Tooltip>
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontSize: 13 }}
            >
              →
            </Typography>
            <Tooltip title={push.deal_sk} placement="top-start">
              <Typography
                variant="body2"
                noWrap
                sx={{ fontSize: 13, color: 'text.primary', minWidth: 0 }}
              >
                {dealLabel}
              </Typography>
            </Tooltip>
            {deal?.type && (
              <Chip
                label={deal.type}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontFamily: 'monospace',
                  bgcolor: 'action.hover',
                  flexShrink: 0,
                }}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ mt: 0.25 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {formatTimestamp(push.pushed_at)}
            </Typography>
            {push.emitted_submissions > 0 && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: 11 }}
              >
                · {push.emitted_submissions} submission
                {push.emitted_submissions === 1 ? '' : 's'}
              </Typography>
            )}
            {push.heartbeat_account_id != null && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: 11, fontFamily: 'monospace' }}
              >
                · HB #{push.heartbeat_account_id}
              </Typography>
            )}
          </Stack>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            justifySelf: { xs: 'start', sm: 'end' },
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {push.pushed_by ?? ''}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2.5, bgcolor: 'action.hover' }}>
        <Stack spacing={1.75} sx={{ pt: 1.5 }}>
          {/* Org / Deal block — resolved names with raw SK/PK underneath for ops */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Org
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {orgLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: 'monospace', fontSize: 11 }}
              >
                {push.org_pk}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Deal
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {dealLabel}
                </Typography>
                {deal?.type && (
                  <Chip
                    label={deal.type}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontFamily: 'monospace',
                      bgcolor: 'action.hover',
                    }}
                  />
                )}
                {deal?.stage && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: 11 }}
                  >
                    · {deal.stage}
                    {deal.status ? ` · ${deal.status}` : ''}
                  </Typography>
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: 'monospace', fontSize: 11 }}
              >
                {push.deal_sk}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                onJumpToDeal(`/deals/${push.org_pk}/${push.deal_sk}/agreement`)
              }
            >
              Open deal →
            </Button>
          </Stack>

          {/* Contract result (Flow A only) */}
          {push.contract_result && (
            <SectionLine label="Contract">
              <Chip
                size="small"
                label={`#${push.contract_result.id ?? '?'}`}
                color={push.contract_result.success ? 'success' : 'error'}
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
              {push.contract_result.error && (
                <Typography variant="caption" color="error.main">
                  {push.contract_result.error}
                </Typography>
              )}
            </SectionLine>
          )}

          {/* Submission summary inline (just counts + first few, full
              detail is on the per-deal timeline) */}
          {push.submission_results.length > 0 && (
            <SectionLine
              label={`Submissions (${push.submission_results.length})`}
            >
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {push.submission_results.slice(0, 6).map((r, i) => (
                  <Chip
                    key={i}
                    size="small"
                    variant="outlined"
                    label={`${r.id != null ? `#${r.id}` : '—'} ${r.service_line ?? ''}`}
                    color={r.success ? 'success' : 'error'}
                    sx={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                ))}
                {push.submission_results.length > 6 && (
                  <Chip
                    size="small"
                    label={`+${push.submission_results.length - 6} more`}
                    sx={{ fontSize: 11 }}
                  />
                )}
              </Stack>
            </SectionLine>
          )}

          {/* Context: HB account id, predecessor, etc. (org + deal are
              already in the resolved block above) */}
          {push.heartbeat_account_id != null && (
            <SectionLine label="Heartbeat account">
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                #{push.heartbeat_account_id}
              </Typography>
              {push.account_source && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ ml: 0.5 }}
                >
                  · {push.account_source}
                </Typography>
              )}
            </SectionLine>
          )}
          {push.predecessor_source && (
            <SectionLine label="Predecessor">
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {push.predecessor_source}
              </Typography>
              {push.predecessor_deal_sk && (
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace', ml: 0.5 }}
                >
                  · {push.predecessor_deal_sk}
                </Typography>
              )}
            </SectionLine>
          )}

          {push.error_message && (
            <Alert severity="error" sx={{ mt: 0.5 }}>
              {push.error_message}
            </Alert>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const SectionLine: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      <Typography
        variant="caption"
        sx={{
          minWidth: 130,
          fontWeight: 600,
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
};

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default HeartbeatPushesAdmin;
